import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { FeaturesBaseServices } from './../../features/services/features-base.services';
import { Rights } from './../../../_models/feature';

import { BaseServices } from './../../kuba.services';
import { SelectItem, ConfirmationService } from 'primeng/api';
import { EmployeeServices } from '../services/employee.services';
import { UserRole } from '../../follow-ups/models/deviation';
import { TranslateService } from '@ngx-translate/core';
import { Table } from 'primeng/table';
import { Subscription } from 'rxjs';
import { KubaGeneralService } from './../../kuba-general.services';

@Component({
  selector: 'employees',
  templateUrl: 'employee-list.component.html',
  standalone: false
})
export class EmployeeListComponent implements OnInit {
  private readonly router = inject(Router);

  createEmployee(): void {
    this.continueToNewEmployee();
  }

  continueToNewEmployee(): void {
    this.router.navigate(['../edit', 0], { relativeTo: this.route });
  }

  //#region variables
  private readonly notifications = inject(NotificationService);
  @ViewChild('employee', { static: false }) employeeTable: Table;
  pdf: any;
  showNotification = false;
  isRightsEnabled = false;
  isManageEmployeeRights = false;
  /**
   * Portal mode flag - indicates if the user is accessing the employee list in portal context
   * Portal users (roleId === '2') have different permissions and see different data
   */
  isPortal = false;
  isUserRole = false;
  isHideEmployee = false;
  employeeobj: any = [];
  statuses: SelectItem[];
  bdepartment: any = [];
  selectedValue: any;
  ShowStatus: any;
  selectedEmployee: any = [];
  addSubDept = false;
  videoUrl: string;
  safeURL: any;
  additionalData: any;
  guest = false;
  loading: boolean;
  BusinessDepartmentId: number = 0;
  RoleIdCommon: number;
  first: number = 0;
  selectedStatus: number = 1;
  private subscriptions: Subscription[] = [];

  // Pagination variables
  currentPage: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;
  isLoading: boolean = false;

  // Filter variables
  nameFilter: string = '';
  numberFilter: string = '';
  emailFilter: string = '';
  mobileFilter: string = '';
  departmentFilter: string = '';
  positionFilter: string = '';
  // Admin-specific filter variables
  categoryFilter: string = '';
  businessFilter: number = null;
  usernameFilter: string = '';
  roleFilter: number = null;

  // Business dropdown options for portal users
  businesses: { label: string; value: number }[] = [];

  withUserOnly: boolean = false;

  // Sorting variables
  sortField: string = 'Name';
  sortOrder: string = 'ASC';

  // Selection tracking for paginated tables
  allSelectedEmployees: Set<number> = new Set(); // Track all selected employee IDs across pages
  headerCheckboxChecked: boolean = false; // Track header checkbox state

  // Debounce subject for filter changes
  private filterChangeSubject = new Subject<string>();
  //#endregion

  public BaseServices = BaseServices;

  //#region constructor
  /**
   * constructor
   * @param employeeServices {employeeServices}
   * @param router {router}
   * @param confirmationService {confirmationService}
   * @param route {route}
   * @param employeeCourseService {employeeCourseService}
   */
  constructor(
    private employeeServices: EmployeeServices,
    private confirmationService: ConfirmationService,
    private route: ActivatedRoute,
    private featuresBaseServices: FeaturesBaseServices,
    private translate: TranslateService,
    private kubaGeneralService: KubaGeneralService
  ) {
    this.featuresBaseServices.setEmployeeEditModeNavigation(false);

    this.subscriptions.push(
      this.translate.stream('FILENAME').subscribe(val => {
        this.additionalData = {
          fileName: val.EMPLOYEE_LIST,
          header: 'Employee list',
          businessId: BaseServices.BusinessId,
          cultureInfo: BaseServices.userCultureInfo(),
          columnNames: [
            { title: 'Employee Number', dataKey: 'Number' },
            { title: 'Name', dataKey: 'Name' },
            { title: 'Department', dataKey: 'DepartmentName' },
            { title: 'SubDepartment', dataKey: 'SubDepartment' },
            { title: 'Mobile', dataKey: 'Mobile' },
            { title: 'Email', dataKey: 'Email' },
            { title: 'Date Birth', dataKey: 'Dob' },
            { title: 'Position Name', dataKey: 'PositionName' },
            { title: 'Status', dataKey: 'StatusKey' },
            { title: 'Notification By Email', datakey: 'NotificationByEmail' },
            { title: 'Notification By SMS', datakey: 'NotificationBySMS' },
            { title: 'Show To Guest', datakey: 'ShowToGuest' },
            { title: 'Show on Dashboard', datakey: 'ShowOnDashboard' },
            { title: 'Approval', datakey: 'Approval' },
            { title: 'HSE Training Confirmed', datakey: 'HseTrainingConfirmed' }
          ]
        };
      })
    );
  }
  //#endregion

  //#region page-events
  /**
   * page load event
   */
  ngOnInit(): void {
    document.querySelector('body').classList.remove('opened');
    this.RoleIdCommon = +BaseServices.roleId;

    // Initialize portal mode based on user role
    this.initializePortalMode();

    // Setup debounced filter changes
    this.setupFilterDebounce();

    // Load business options for portal users
    this.loadBusinessOptions();

    this.loadEmployees();
    this.selectedValue = '1';
    this.subscriptions.push(
      this.translate.stream('SELECT_STATUS_DROP').subscribe(val => {
        this.statuses = [];
        this.statuses.push(
          { label: val.SELECT, value: null },
          { label: val.INACTIVE, value: 2 },
          { label: val.ACTIVE, value: 1 }
        );
      })
    );
    this.selectedStatus = 1; // Default to active status
    this.onAddNewEmployee();
    this.onHideEmployeeList();
  }

  ngAfterViewInit() {
    this.first =
      parseInt(sessionStorage.getItem('Pageindex')!) *
        parseInt(sessionStorage.getItem('PageRow')!) -
      parseInt(sessionStorage.getItem('PageRow')!);
  }
  paginate(event: any) {
    sessionStorage.setItem(
      'Pageindex',
      JSON.stringify(event.first / event.rows + 1)
    );
    sessionStorage.setItem('PageRow', JSON.stringify(event.rows));
  }
  //#region methods
  /**
   * get employee by business
   */
  getEmployee() {
    // Use pagination instead of old list method
    this.loadEmployees();
  }

  /**
   * Load employees with pagination
   * Handles different service calls based on user role:
   * - Admin users (roleId === '1'): getPagedAdminList
   * - Portal users (roleId === '2'): getPagedPortalList
   * - Business users: getPagedList
   */
  loadEmployees() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    const filter = {
      PageNumber: this.currentPage,
      PageSize: this.pageSize,
      SortColumn: this.sortField,
      SortDirection: this.sortOrder,
      Name: this.nameFilter || undefined,
      Number: this.numberFilter || undefined,
      Email: this.emailFilter || undefined,
      Mobile: this.mobileFilter || undefined,
      Status: this.selectedStatus || undefined,
      Department: this.departmentFilter || undefined,
      Position: this.positionFilter || undefined,
      Category: this.categoryFilter || undefined,
      BusinessId: this.businessFilter || undefined,
      UserName: this.usernameFilter || undefined,
      RoleId: this.roleFilter || undefined,
      WithUserOnly: this.withUserOnly
    };
    let serviceCall: Observable<any>;
    if (BaseServices.roleId === '1') {
      // Admin users can see all employees across all businesses
      serviceCall = this.employeeServices.getPagedAdminList(filter);
    } else if (this.isPortal) {
      // Portal users see employees from multiple businesses within their portal
      const portalId = this.getPortalId();
      serviceCall = this.employeeServices.getPagedPortalList(portalId, filter);
    } else {
      // Business users see only employees from their own business
      serviceCall = this.employeeServices.getPagedList(
        BaseServices.BusinessId,
        filter
      );
    }
    this.subscriptions.push(
      serviceCall.subscribe(
        result => {
          this.employeeobj = result.Items || [];
          this.totalItems = result.TotalCount || 0;
          this.totalPages = result.TotalPages || 0;
          this.isLoading = false;

          // Reset header checkbox state when new data is loaded
          this.headerCheckboxChecked = false;
        },
        error => {
          this.isLoading = false;
        }
      )
    );
  }

  /**
   * Handle sort event from table
   */
  onSort(event: any) {
    this.sortField = event.field;
    this.sortOrder = event.order === 1 ? 'ASC' : 'DESC';
    this.loadEmployees();
  }

  /**
   * Initialize portal mode based on user role
   * Portal users (roleId === '2') have different permissions and data access
   */
  private initializePortalMode(): void {
    this.isPortal = BaseServices.roleId === '2';

    if (this.isPortal) {
      // Portal users see employees from multiple businesses
      // They have limited permissions and see different UI elements
      console.log(
        'Portal mode initialized - User is accessing employee list in portal context'
      );
    }
  }

  /**
   * Get portal ID for portal users
   */
  private getPortalId(): number {
    return BaseServices.PortalId;
  }

  /**
   * Check if current user has portal-specific permissions
   * Portal users have limited permissions compared to admin users
   */
  private hasPortalPermissions(): boolean {
    return this.isPortal && BaseServices.roleId === '2';
  }

  /**
   * Load business options for portal users
   * Fetches business list from backend API
   */
  private loadBusinessOptions(): void {
    if (this.isPortal) {
      this.businesses = [
        { label: this.translate.instant('SELECT'), value: null }
      ];

      this.subscriptions.push(
        this.kubaGeneralService.getBusinessNames().subscribe({
          next: (businessList: any[]) => {
            if (businessList && Array.isArray(businessList)) {
              businessList.forEach((business: any) => {
                this.businesses.push({
                  label: business.Name,
                  value: business.Id
                });
              });
            }
          },
          error: error => {
            console.error('Error loading business options:', error);
          }
        })
      );
    }
  }

  /**
   * Handle business dropdown change for portal users
   */
  onBusinessChanged(e: any, businessDropdown: any): void {
    this.businessFilter = e.value;
    this.clearAllSelections(); // Clear selections when filters change
    this.triggerFilterChange();
  }

  /**
   * Handle status dropdown change event
   * @param e {any}
   * @param statusDropdown {any}
   */
  onStatusChanged(e: any, statusDropdown: any) {
    // Always trigger filter change for status updates, regardless of portal or non-portal
    this.clearAllSelections(); // Clear selections when filters change
    this.triggerFilterChange();
  }

  /**
   * Get portal-specific business filter options
   * Portal users can filter by business name
   */
  private getPortalBusinessFilter(): string | undefined {
    if (this.isPortal && this.businessFilter) {
      return this.businessFilter.toString();
    }
    return undefined;
  }

  /**
   * Handle page change
   */
  onPageChange(event: any) {
    this.currentPage = event.first / event.rows + 1;
    this.pageSize = event.rows;
    this.loadEmployees();
  }

  /**
   * Handle individual row selection change
   * @param event Selection event from PrimeNG table
   */
  onRowSelectionChange(event: any) {
    if (event.checked) {
      // Add to global selection
      this.allSelectedEmployees.add(event.data.Id);
    } else {
      // Remove from global selection
      this.allSelectedEmployees.delete(event.data.Id);
    }

    // Update the selectedEmployee array for current page
    this.selectedEmployee = this.employeeobj.filter((emp: any) =>
      this.allSelectedEmployees.has(emp.Id)
    );
  }

  /**
   * Handle header checkbox selection change (select all on current page)
   * @param event Selection event from PrimeNG table
   */
  onHeaderSelectionChange(event: any) {
    this.headerCheckboxChecked = event.checked;

    if (event.checked) {
      // Select all employees on current page
      this.employeeobj.forEach((emp: any) => {
        this.allSelectedEmployees.add(emp.Id);
      });
    } else {
      // Deselect all employees on current page
      this.employeeobj.forEach((emp: any) => {
        this.allSelectedEmployees.delete(emp.Id);
      });
    }

    // Update the selectedEmployee array
    this.selectedEmployee = this.employeeobj.filter((emp: any) =>
      this.allSelectedEmployees.has(emp.Id)
    );
  }

  /**
   * Check if all employees on current page are selected
   */
  isAllSelectedOnCurrentPage(): boolean {
    if (this.employeeobj.length === 0) return false;
    return this.employeeobj.every((emp: any) =>
      this.allSelectedEmployees.has(emp.Id)
    );
  }

  /**
   * Check if some employees on current page are selected
   */
  isSomeSelectedOnCurrentPage(): boolean {
    if (this.employeeobj.length === 0) return false;
    const selectedOnPage = this.employeeobj.filter((emp: any) =>
      this.allSelectedEmployees.has(emp.Id)
    );
    return (
      selectedOnPage.length > 0 &&
      selectedOnPage.length < this.employeeobj.length
    );
  }

  /**
   * Clear all selections (useful when filters change)
   */
  clearAllSelections() {
    this.allSelectedEmployees.clear();
    this.selectedEmployee = [];
    this.headerCheckboxChecked = false;
  }

  /**
   * Setup debounced filter changes
   */
  private setupFilterDebounce() {
    this.subscriptions.push(
      this.filterChangeSubject
        .pipe(
          debounceTime(800) // 800ms debounce for typing
        )
        .subscribe(() => {
          this.currentPage = 1;
          this.loadEmployees();
        })
    );
  }

  /**
   * Trigger debounced filter change
   */
  private triggerFilterChange() {
    this.filterChangeSubject.next('filter');
  }

  /**
   * Handle filter changes from table
   */
  onFilterChange() {
    this.clearAllSelections(); // Clear selections when filters change
    this.triggerFilterChange();
  }

  /**
   * Handle individual filter changes with proper value binding and debounce
   */
  onNameFilterChange(value: string) {
    this.nameFilter = value;
    this.clearAllSelections(); // Clear selections when filters change
    this.triggerFilterChange();
  }

  onNumberFilterChange(value: string) {
    this.numberFilter = value;
    this.clearAllSelections(); // Clear selections when filters change
    this.triggerFilterChange();
  }

  onEmailFilterChange(value: string) {
    this.emailFilter = value;
    this.clearAllSelections(); // Clear selections when filters change
    this.triggerFilterChange();
  }

  onMobileFilterChange(value: string) {
    this.mobileFilter = value;
    this.clearAllSelections(); // Clear selections when filters change
    this.triggerFilterChange();
  }

  onDepartmentFilterChange(value: string) {
    this.departmentFilter = value;
    this.clearAllSelections(); // Clear selections when filters change
    this.triggerFilterChange();
  }

  onPositionFilterChange(value: string) {
    this.positionFilter = value;
    this.clearAllSelections(); // Clear selections when filters change
    this.triggerFilterChange();
  }

  /**
   * set with user only mode
   */
  setWithUserOnlyMode(mode: boolean) {
    this.withUserOnly = mode;
    this.triggerFilterChange();
  }

  /**
   * bind status dropdown
   */
  refreshStatusDropDown() {
    this.selectedValue = null;
    this.subscriptions.push(
      this.translate.stream('SELECT_STATUS_DROP').subscribe(val => {
        this.statuses = [];
        this.statuses.push(
          { label: val.SELECT, value: null },
          { label: val.INACTIVE, value: 2 },
          { label: val.ACTIVE, value: 1 }
        );
      })
    );
  }
  //#region performance improvement

  //#endregion

  /**
   * Check if any employees are selected
   */
  hasSelectedEmployees(): boolean {
    return this.allSelectedEmployees.size > 0;
  }

  /**
   * employee multi delete
   * @param selectedEmployee
   */
  deleteEmployee(selectedEmployee: any) {
    if (!this.hasSelectedEmployees()) {
      this.showNotification = true;
      return;
    }

    let employeeIds: any = [];
    // Get all selected employee IDs from the global selection
    this.allSelectedEmployees.forEach((employeeId: number) => {
      employeeIds.push(employeeId);
    });

    if (employeeIds.length > 0) {
      this.confirmationService.confirm({
        message: this.translate.instant('DELETE_THIS_RECORD'),
        accept: () => {
          this.subscriptions.push(
            this.employeeServices
              .deleteMultipleEmployee(employeeIds)
              .subscribe(() => {
                this.selectedEmployee = [];
                this.allSelectedEmployees.clear(); // Clear global selection
                this.headerCheckboxChecked = false; // Reset header checkbox
                this.notifications.success('UI_NOTIFICATIONS.DELETED');
                this.refreshStatusDropDown();
                // Reload employees with pagination after deletion
                this.loadEmployees();
              })
          );
        }
      });
    } else {
      this.showNotification = true;
    }
  }

  /**
   * edit status
   * @param Id
   * @param Status
   */
  editStatus(Id: any, Status: string) {
    let currentStatus = Status === 'ACTIVE' ? 1 : 2;
    if (currentStatus == 1) {
      this.confirmationService.confirm({
        message: this.translate.instant('EDIT_INACTIVE_THIS_EMPLOYEE'),
        accept: () => {
          this.loading = true;
          this.subscriptions.push(
            this.employeeServices
              .statusUpdate(Id, currentStatus)
              .subscribe(() => {
                this.loadEmployees();
                this.loading = false;
              })
          );
        }
      });
    } else {
      this.confirmationService.confirm({
        message: this.translate.instant('EDIT_ACTIVE_THIS_EMPLOYEE'),
        accept: () => {
          this.loading = true;
          this.subscriptions.push(
            this.employeeServices
              .statusUpdate(Id, currentStatus)
              .subscribe(() => {
                this.loadEmployees();
                this.loading = false;
              })
          );
        }
      });
    }
  }

  /**
   * Get all employees for export with filters and sorting
   */
  getAllEmployeesForExport = (): Promise<any[]> => {
    const filter = {
      PageNumber: 1,
      PageSize: 100000, // maximum possible
      SortColumn: this.sortField,
      SortDirection: this.sortOrder,
      Name: this.nameFilter || undefined,
      Number: this.numberFilter || undefined,
      Email: this.emailFilter || undefined,
      Mobile: this.mobileFilter || undefined,
      Status: this.selectedStatus || undefined,
      Department: this.departmentFilter || undefined,
      Position: this.positionFilter || undefined,
      Category: this.categoryFilter || undefined,
      BusinessId: this.businessFilter || undefined,
      UserName: this.usernameFilter || undefined,
      RoleId: this.roleFilter || undefined,
      WithUserOnly: this.withUserOnly
    };
    let serviceCall: Observable<any>;
    if (BaseServices.roleId === '1') {
      serviceCall = this.employeeServices.getPagedAdminList(filter);
    } else if (BaseServices.roleId === '2') {
      const portalId = this.getPortalId();
      serviceCall = this.employeeServices.getPagedPortalList(portalId, filter);
    } else {
      serviceCall = this.employeeServices.getPagedList(
        BaseServices.BusinessId,
        filter
      );
    }
    return new Promise((resolve, reject) => {
      serviceCall.subscribe({
        next: result => resolve(result.Items || []),
        error: err => reject(err)
      });
    });
  };
  //#endregion

  //#region control-events
  /**
   * department filter
   * @param e
   * @param departmentDropdown
   */
  onDepartmentChanged(e: any, departmentDropdown: any) {
    if (e.value) {
      // custom filter datatable
      this.employeeTable.filter(
        departmentDropdown.selectedOption.label,
        'DepartmentName',
        'equals'
      );
    } else {
      this.employeeTable.reset();
    }
  }

  /**
   * hide/show new employee
   */
  onAddNewEmployee() {
    let userRightsId = Rights.ADD_NEW_EMPLOYEE;
    if (+BaseServices.roleId === UserRole.USER) {
      this.isManageEmployeeRights = BaseServices.checkUserRights(
        userRightsId,
        BaseServices.UserRole
      );
    } else if (+BaseServices.roleId === UserRole.GUEST) {
      this.isManageEmployeeRights = false;
    } else {
      this.isManageEmployeeRights = true;
    }
  }
  /**
   * hide/show employee list
   */
  onHideEmployeeList() {
    let userRightsId = Rights.HIDE_EMPLOYEE_LIST;
    let role = BaseServices.UserRole;
    if (role === 'User' || role === 'Guest') {
      this.isHideEmployee = BaseServices.checkUserRights(userRightsId, role);
      if (this.isHideEmployee === true) {
        this.isRightsEnabled = true;
      }
    } else {
      this.isHideEmployee = false;
    }
  }

  departmentEditRights() {
    let userRightsId = Rights.EDIT_DEPARTMENT_EMPLOYEE_RIGHTS;
    let role = BaseServices.UserRole;
    if (role === 'User') {
      let dataaaaaa = BaseServices.checkUserRights(userRightsId, role);
      this.BusinessDepartmentId = BaseServices.BusDepartment;
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }

  //   Profile image display:
  employee = {
    Avatar: '/assets/icons/default_profile.jpg' // default profile image
  };

  /**
   * Checks if the given URL has a valid image extension and is not a default image.
   * @param url - The URL to check.
   * @returns `true` if the URL has a valid image extension and is not default, otherwise `false`.
   * Used to display the default profile image if the user has not uploaded a profile image.
   */
  onAvatarError(event: Event): void {
    const image = event.target as HTMLImageElement;
    if (image.getAttribute('src') !== '/assets/icons/default_profile.jpg') {
      image.src = '/assets/icons/default_profile.jpg';
    }
  }

  isValidImageExtension(url: string): boolean {
    if (!url || url.includes('default_profile.jpg')) return false;
    const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
    try {
      const pathname = new URL(url).pathname;
      const extension = pathname.split('.').pop()?.toLowerCase();
      return validExtensions.includes(extension || '');
    } catch {
      return false;
    }
  }
  //#endregion
}
